import { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { BrandDocument } from '@models/brand';
import { CategoryDocument } from '@models/category';
import Accordion from '@ui/Accordion';
import Checkbox from '@ui/Checkbox';
import Input from '@ui/Input';
import Toggle from '@ui/Toggle';
import { getGenderOptions } from '@util/createListingHelpers';
import { useCategoryFilters } from '@util/hooks/useCategoryFilters';
import { getCountryLabelFromCode } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { FacetCounts } from 'models/product';
import {
  BOOT_SIZES,
  Gender,
  LETTER_SIZES,
  PANT_SIZES,
  RideType,
  rideTypeChoices,
} from 'models/shared';
import { UserSizes } from 'models/user';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import CheckboxFilterSection from './sections/CheckboxFilterSection';
import InYourSizeFilterSection from './sections/InYourSizeFilterSection';
import PriceRangeFilterSection from './sections/PriceRangeFilterSection';
export interface FilterItem {
  key: string;
  label: string;
  selected?: boolean;
}

interface CategoryFilterProps {
  categories: CategoryDocument[];
  filterState: CategoryPageSideFilters;
  setFilterState: (filters: CategoryPageSideFilters) => void;
  hideCats?: boolean;
  hideBrands?: boolean;
  hideGender?: boolean;
  showBootSizes?: boolean;
  showShirtSizes?: boolean;
  showPantSizes?: boolean;
  showOEMSection?: boolean;
  showSizeSection?: boolean;
  showSellerType?: boolean;
  showColorSection?: boolean;
  showRideTypes?: boolean;
  showSubCategories?: boolean;
  brand?: BrandDocument;
  allBrands?: string[];
  categoryBrands?: string[];
  genders?: Gender[];
  currentCat?: CategoryDocument | null;
  startAllClosed?: boolean;
  facet_counts?: FacetCounts;
  isMostlyTires?: boolean;
}

const CategoryFilters = ({
  categories,
  filterState,
  setFilterState,
  hideCats,
  hideBrands,
  hideGender,
  allBrands,
  showShirtSizes,
  showOEMSection,
  showSizeSection,
  showRideTypes,
  showSubCategories,
  currentCat,
  startAllClosed,
  facet_counts,
  isMostlyTires,
}: CategoryFilterProps) => {
  const { userDoc } = useAuth();

  const router = useRouter();
  const pathname = usePathname();

  const {
    handlePricesChange,
    handleBrandsChange,
    handleRideTypeChange,
    handleGenderChange,
    handleLetterSizeChange,
    handleShipToChange,
    handleShipFromChange,
    handleSaleChange,
    handleRecentlySoldChange,
    handleCategoryChange,
    getSizeTitle,
    getBrandFilterOptions,
    brandFilter,
    setBrandFilter,
    aspectRatioFilter,
    setAspectRatioFilter,
    getCatUrl,
    formattedFacetCounts,
    countryCodes,
    handleNumberSizeChange,
    handleAspectRatioChange,
    handleRimDiameterChange,
    handleMountingPositionChange,
    handleTireDiameterChange,
    handleWidthChange,
    handleConstructionChange,
  } = useCategoryFilters({
    filterState,
    setFilterState,
    currentCat,
    allBrands,
    facet_counts,
    categories,
  });

  const mainCat = facet_counts?.category?.[0]?.value ?? '';
  const cat1 = facet_counts?.category1?.[0]?.value ?? '';
  const cat2 = facet_counts?.category2?.[0]?.value ?? '';
  return (
    <div className="flex w-full min-w-[25rem] flex-col rounded-brand bg-inherit sm:w-[38rem]">
      {userDoc?.country?.code && userDoc.country.code !== 'US' && (
        <div className="my-2 flex w-full justify-between rounded-2xl border-2 border-brand-secondary bg-brand-secondary-light px-3 py-4">
          <span className="font-semibold">
            {`Ship to My Country (${userDoc?.country.code})`}
          </span>
          <Toggle
            enabled={
              filterState.shipTo?.includes(userDoc?.country.code) ?? false
            }
            onChange={() => {
              const newArr = [...(filterState.shipTo ?? [])];
              if (!newArr.includes(userDoc?.country.code)) {
                newArr.push(userDoc?.country.code);
              } else {
                newArr.splice(newArr.indexOf(userDoc?.country.code), 1); //deleting
              }
              handleShipToChange(newArr);
            }}
          />
        </div>
      )}
      {showSizeSection && (
        <InYourSizeFilterSection
          selectedSizes={{
            key: currentCat?.name.toLowerCase() as keyof UserSizes,
            value:
              currentCat?.id.includes('pants') ||
              currentCat?.id.includes('boots')
                ? filterState.numberSizes?.[0]
                : currentCat?.id.includes('gear-combos')
                ? filterState.letterSizes?.[0] +
                  '/' +
                  filterState.numberSizes?.[0]
                : filterState.letterSizes?.[0],
          }}
          handleSizeChange={(size: {
            key: keyof UserSizes | 'gear combos';
            value: string;
          }) => {
            const { key, value } = size;
            if (key === 'gloves') {
              router.replace(`/shop/riding-gear/gear/size-${value}-gloves`);
            } else if (key === 'boots') {
              router.replace(`/shop/riding-gear/size-${value}-boots`);
            } else if (key === 'pants') {
              router.replace(`/shop/riding-gear/gear/size-${value}-pants`);
            } else if (key === 'jersey') {
              router.replace(`/shop/riding-gear/gear/size-${value}-jerseys`);
            } else if (key === 'helmet') {
              router.replace(`/shop/riding-gear/size-${value}-helmets`);
            } else if (key === 'gear combos') {
              const values = value.split('/');
              router.replace(
                `/shop/riding-gear/gear/size-${values[0]}-size-${values[1]}-gear-combos`
              );
            }
          }}
        />
      )}
      {!hideCats && !!formattedFacetCounts.hierarchy.otherCats?.length && (
        <div className="border-b border-zinc-300">
          <Accordion
            title={'Categories'}
            titleClassName="!text-[1.8rem] !font-semibold"
            startOpen={!startAllClosed}
          >
            <div className="flex max-h-[45rem] flex-col gap-4 overflow-y-auto overflow-x-hidden px-[1rem] pb-[2rem]">
              {formattedFacetCounts.hierarchy.otherCats
                ?.sort((a, b) => b.count - a.count)
                .map((cat, i) => {
                  return (
                    <button
                      key={cat.value + cat.order}
                      type="button"
                      className="text-left text-[1.6rem] font-medium transition-all duration-150 ease-linear hover:font-semibold hover:text-brand-secondary"
                      onClick={() => {
                        handleCategoryChange(
                          cat.value,
                          cat.order as
                            | 'category'
                            | 'category1'
                            | 'category2'
                            | 'category3'
                        );
                      }}
                    >
                      {cat.value} ({cat.count})
                    </button>
                  );
                })}
            </div>
          </Accordion>
        </div>
      )}
      {showSubCategories && (
        <div className="border-b border-zinc-300">
          <Accordion
            title={'Categories'}
            titleClassName="!text-[1.8rem] !font-semibold"
            startOpen={!startAllClosed}
          >
            <div className="flex max-h-[45rem] flex-col gap-4 overflow-y-auto overflow-x-hidden px-[1rem] pb-[2rem]">
              {categories.map((cat, i) => {
                return (
                  <Link
                    key={cat.id}
                    href={getCatUrl(filterState, cat)}
                    className="text-left text-[1.6rem] font-medium transition-all duration-150 ease-linear hover:font-semibold hover:text-brand-secondary"
                  >
                    {cat.name}
                  </Link>
                );
              })}
            </div>
          </Accordion>
        </div>
      )}
      {showOEMSection && (
        <div className="flex w-full justify-between border-b border-zinc-300 px-3 py-4">
          <span className="text-[1.8rem] font-semibold">OEM Parts</span>

          <Toggle
            enabled={!!filterState.brands?.includes('OEM')}
            onChange={() => {
              const newArr = [...(filterState.brands ?? [])];
              if (!newArr.includes('OEM')) {
                newArr.push('OEM');
              } else {
                newArr.splice(newArr.indexOf('OEM'), 1); //deleting
              }
              handleBrandsChange(newArr);

              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }}
          />
          <Link
            hidden
            // immediately invoke this function to return a string
            href={(() => {
              const segments = pathname?.split('/') ?? ['/'];
              const path = pathname
                ?.split('/')
                .slice(0, segments.length - 1) ?? ['/'];
              const lastCat = pathname?.split('/')[segments.length - 1];
              return (
                path.join('/') + '/' + 'oem' + (lastCat ? '-' + lastCat : '')
              );
            })()}
          >
            OEM
          </Link>
        </div>
      )}
      {!!rideTypeChoices.length && showRideTypes && (
        <CheckboxFilterSection
          title="Ride Types"
          startOpen={!startAllClosed}
          filterOptions={rideTypeChoices.map((rideType) => ({
            value: rideType,
            key: rideType,
            label: rideType,
          }))}
          onChange={(values) => handleRideTypeChange(values as RideType[])}
          filterState={filterState.rideTypes ?? []}
        />
      )}
      {!!facet_counts?.['specs.Width']?.length && isMostlyTires && (
        <CheckboxFilterSection
          title="Width"
          startOpen={!startAllClosed}
          filterOptions={facet_counts?.['specs.Width']?.map((item) => ({
            key: item.value,
            label: item.value,
            count: item.count,
          }))}
          onChange={(values) => handleWidthChange(values)}
          filterState={filterState.widths ?? []}
          facet_counts={facet_counts?.['specs.Width']}
          showSelectedFirst
        />
      )}
      {!!facet_counts?.['specs.Aspect Ratio']?.length && isMostlyTires && (
        <CheckboxFilterSection
          title="Aspect Ratio"
          startOpen={!startAllClosed}
          filterOptions={facet_counts?.['specs.Aspect Ratio']?.map((item) => ({
            key: item.value,
            label: item.value,
            count: item.count,
          }))}
          onChange={(values) => handleAspectRatioChange(values)}
          filterState={filterState.aspectRatios ?? []}
          facet_counts={facet_counts?.['specs.Aspect Ratio']}
          showSelectedFirst
        >
          <div className="mb-4 p-4">
            <Input
              placeholder={'Search Tire Sizes'}
              type={'search'}
              iconClassName="!h-6 !w-6"
              value={aspectRatioFilter}
              onChange={(e) => setAspectRatioFilter(e.target.value)}
              className="!h-[3.8rem] !rounded-[1.5rem]"
            />
          </div>
        </CheckboxFilterSection>
      )}
      {!!facet_counts?.['specs.Rim Diameter']?.length && isMostlyTires && (
        <CheckboxFilterSection
          title="Rim Diameter"
          startOpen={!startAllClosed}
          filterOptions={facet_counts?.['specs.Rim Diameter']?.map((item) => ({
            key: item.value,
            label: item.value,
            count: item.count,
          }))}
          onChange={(values) => handleRimDiameterChange(values)}
          filterState={filterState.rimDiameters ?? []}
          facet_counts={facet_counts?.['specs.Rim Diameter']}
          showSelectedFirst
        />
      )}
      {!!facet_counts?.['specs.Construction']?.length && isMostlyTires && (
        <CheckboxFilterSection
          title="Construction"
          startOpen={!startAllClosed}
          filterOptions={facet_counts?.['specs.Construction']?.map((item) => ({
            key: item.value,
            label: item.value,
            count: item.count,
          }))}
          onChange={(values) => handleConstructionChange(values)}
          filterState={filterState.construction ?? []}
          facet_counts={facet_counts?.['specs.Construction']}
          showSelectedFirst
        />
      )}
      {!!facet_counts?.['specs.Mounting Position']?.length && isMostlyTires && (
        <CheckboxFilterSection
          title="Mounting Position"
          startOpen={!startAllClosed}
          filterOptions={facet_counts?.['specs.Mounting Position']?.map(
            (item) => ({
              key: item.value,
              label: item.value,
              count: item.count,
            })
          )}
          onChange={(values) => handleMountingPositionChange(values)}
          filterState={filterState.mountingPositions ?? []}
          facet_counts={facet_counts?.['specs.Mounting Position']}
          showSelectedFirst
        />
      )}

      {!!facet_counts?.['specs.Tire Diameter']?.length && isMostlyTires && (
        <CheckboxFilterSection
          title="Tire Diameter"
          startOpen={!startAllClosed}
          filterOptions={facet_counts?.['specs.Tire Diameter']?.map((item) => ({
            key: item.value,
            label: item.value,
            count: item.count,
          }))}
          onChange={(values) => handleTireDiameterChange(values)}
          filterState={filterState.tireDiameters ?? []}
          facet_counts={facet_counts?.['specs.Tire Diameter']}
          showSelectedFirst
        />
      )}

      {!hideGender && (
        <CheckboxFilterSection
          title="Gender / Age"
          startOpen={!startAllClosed}
          filterOptions={getGenderOptions().map((g) => ({
            value: g.value,
            key: g.value,
            label: g.label,
          }))}
          onChange={(values) => handleGenderChange(values)}
          filterState={filterState.genders ?? []}
        />
      )}
      {showShirtSizes && (
        <CheckboxFilterSection
          title={getSizeTitle()}
          filterOptions={LETTER_SIZES.map((g) => ({
            key: g + '',
            label: g + '',
          }))}
          onChange={handleLetterSizeChange}
          startOpen={!startAllClosed}
          filterState={filterState.letterSizes ?? []}
          facet_counts={facet_counts?.['variations.size']}
        />
      )}
      {mainCat === 'Riding Gear' && cat1 === 'Boots' && (
        <CheckboxFilterSection
          title="Boot Size"
          filterOptions={BOOT_SIZES[
            (filterState.genders?.length === 1
              ? filterState.genders[0]
              : 'U') as Gender
          ].map((g) => ({ key: g + '', label: g + '' }))}
          onChange={handleNumberSizeChange}
          filterState={filterState.numberSizes ?? []}
          startOpen={!startAllClosed}
        />
      )}
      {mainCat === 'Riding Gear' && cat2 === 'Pants' && (
        <CheckboxFilterSection
          title="Pant Size"
          filterOptions={PANT_SIZES[
            (filterState.genders?.length === 1
              ? filterState.genders[0]
              : 'U') as Gender
          ].map((g) => ({ key: g + '', label: g + '' }))}
          onChange={handleNumberSizeChange}
          filterState={filterState.numberSizes ?? []}
          startOpen={!startAllClosed}
        />
      )}
      {!hideBrands && !filterState.brands?.includes('OEM') && (
        <CheckboxFilterSection
          title="Brands"
          filterOptions={getBrandFilterOptions()}
          onChange={handleBrandsChange}
          filterState={filterState.brands ?? []}
          startOpen={!startAllClosed}
          facet_counts={facet_counts?.brand}
          noFiltersMessage="No brands found"
          noFiltersClassName="py-8"
        >
          <div className="mb-4 p-4">
            <Input
              placeholder={'Search Brands'}
              type={'search'}
              iconClassName="!h-6 !w-6"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="!h-[3.8rem] !rounded-[1.5rem]"
            />
          </div>
        </CheckboxFilterSection>
      )}

      <div className="mt-4 border-b border-zinc-300">
        <PriceRangeFilterSection
          min={filterState.minPrice}
          max={filterState.maxPrice}
          title="Price"
          titleClassName="!text-[1.8rem]"
          onChange={handlePricesChange}
          startOpen={
            !startAllClosed || !!filterState.minPrice || !!filterState.maxPrice
          }
        />
      </div>

      <CheckboxFilterSection
        startOpen={!startAllClosed}
        title="Ships To"
        filterOptions={countryCodes.map((code) => ({
          key: code,
          value: code,
          label: getCountryLabelFromCode(code),
        }))}
        onChange={handleShipToChange}
        filterState={filterState.shipTo ?? []}
      />

      <CheckboxFilterSection
        startOpen={!startAllClosed}
        title="Ships From"
        filterOptions={countryCodes.map((code) => ({
          key: code,
          value: code,
          label: getCountryLabelFromCode(code),
        }))}
        onChange={handleShipFromChange}
        filterState={filterState.shipFrom ?? []}
      />
      <div className="mb-4 border-b border-zinc-300">
        <Accordion
          title="Show Only"
          startOpen={!startAllClosed}
          titleClassName="!text-[1.8rem]"
        >
          <div className="flex flex-col gap-y-2 p-[1rem]">
            <Checkbox
              label="On Sale"
              onChange={() => {
                handleSaleChange(!filterState.onsale);
              }}
              selected={!!filterState.onsale}
            />

            <Checkbox
              label="Sold Items"
              onChange={() => {
                handleRecentlySoldChange(!filterState.recentlySold);
              }}
              selected={!!filterState.recentlySold}
            />
          </div>
        </Accordion>
      </div>
    </div>
  );
};

export default CategoryFilters;
